import { TimelineReferenceSchema } from '@/timeline/references/store/schemas'
import { TimelineSourceRef, TimelineSourceRefSchema } from '@/timeline/sources/schemas'
import { parseISODate } from '@/util/zod/validators'
import { z } from 'zod'

// A single event field value paired with its reference id
export const EventFieldItemSchema = <T extends z.ZodTypeAny>(valueSchema: T) =>
  z.object({
    reference_id: z.string().uuid(),
    value: valueSchema,
    raw_fact: z.string().nullable().optional(),
    date_parse_failed: z.boolean().optional(),
  })

export type EventFieldItem<T> = {
  reference_id: string
  value: T
  raw_fact?: string | null
  date_parse_failed?: boolean
}

// UI-ready event field item
export type UIEventFieldItem = {
  reference_id: string
  text: string
}

// UI-ready timeline event
export type UITimelineEvent = {
  id: string
  eventDate: UIEventFieldItem
  description: UIEventFieldItem[]
  source: TimelineSourceRef
  pages: number[]
}

export const TimelineEventSchema = z
  .object({
    id: z.string().uuid(),
    event_date: EventFieldItemSchema(z.preprocess((val, ctx) => parseISODate(val, ctx), z.string().nullable())),
    // Backend serializes this as `description_sentences` (TimelineEventView). We map it to
    // the `description` field consumers already read via the transform below.
    description_sentences: z.array(EventFieldItemSchema(z.string())),
    file_path: z.string(),
    source: TimelineSourceRefSchema,
    pages: z.array(z.number().int().nonnegative()),
  })
  .transform(({ description_sentences, pages, ...rest }) => ({
    ...rest,
    description: description_sentences,
    // Backend emits 1-based page numbers (PaxtonHighlightBlock.page_number is documented
    // "Canonical 1-based document page number"), but the shared viewer layer is 0-based:
    // PageCitation renders `page + 1` for its label and the PDF viewer's jumpToPage is
    // 0-indexed. Convert to 0-based here — at the timeline data boundary — so the label shows
    // the true page AND a click lands on it, without touching the shared PageCitation / PdfViewer.
    pages: pages.map((page) => page - 1),
  }))

export type TimelineEvent = z.infer<typeof TimelineEventSchema>

export const TimelineEventsResponseSchema = z.object({
  events: z.array(TimelineEventSchema),
  references: z.record(z.string().uuid(), TimelineReferenceSchema),
})

export type TimelineEventsResponse = z.infer<typeof TimelineEventsResponseSchema>
